import { useEffect, useState } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
    ShieldCheck, LayoutDashboard, ShoppingCart, Package, Receipt,
    Settings, BarChart3, Clock, HelpCircle, Boxes, Truck, MoreHorizontal, ArrowLeft, LogOut
} from 'lucide-react';

const MOBILE_NAV = [
    { path: '/dashboard', label: 'Home', icon: LayoutDashboard },
    { path: '/pos', label: 'POS', icon: ShoppingCart },
    { path: '/products', label: 'Products', icon: Package },
    { path: '/sales', label: 'Sales', icon: Receipt },
    { path: '/settings', label: 'Settings', icon: Settings },
];

const MORE_NAV = [
    { path: '/inventory', label: 'Inventory', icon: Boxes },
    { path: '/suppliers', label: 'Suppliers', icon: Truck },
    { path: '/reports', label: 'Reports', icon: BarChart3 },
    { path: '/timeclock', label: 'Time Clock', icon: Clock },
    { path: '/help', label: 'Help', icon: HelpCircle },
];

export function Layout({ children, title }) {
    const { restoreAdmin, user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const isImpersonating = !!localStorage.getItem('admin_token_backup');
    const isSuperAdmin = user?.role === 'admin' && !user?.store_id;
    
    // Fix: Use state for mobile detection with resize listener
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [showMore, setShowMore] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const sidebarBg = isSuperAdmin ? 'hsl(265,40%,5%)' : 'hsl(222,47%,5%)';

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 1024);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const mainPaths = MOBILE_NAV.map(n => n.path);
    const isSecondaryPage = !mainPaths.includes(location.pathname);

    useEffect(() => { setShowMore(false); }, [location.pathname]);

    const handleExitSupport = () => {
        const backup = localStorage.getItem('admin_token_backup');
        if (restoreAdmin) { restoreAdmin(); }
        else if (backup) {
            localStorage.setItem('token', backup);
            localStorage.removeItem('admin_token_backup');
            window.location.href = '/admin';
        }
    };

    if (isMobile) {
        return (
            <div style={{ minHeight: '100vh', background: 'var(--background)' }}>
                {isImpersonating && (
                    <div style={{ background: '#f59e0b', color: '#451a03', padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, fontWeight: 600 }}>
                        <span>Support Mode Active</span>
                        <button onClick={handleExitSupport} style={{ background: 'rgba(0,0,0,0.15)', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', color: '#451a03', fontWeight: 600 }}>Exit</button>
                    </div>
                )}

                {/* Mobile top bar */}
                <div style={{
                    position: 'fixed', top: isImpersonating ? 36 : 0, left: 0, right: 0, zIndex: 40,
                    height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: sidebarBg, borderBottom: '1px solid rgba(255,255,255,0.06)',
                    paddingLeft: 12, paddingRight: 12
                }}>
                    {isSecondaryPage ? (
                        <button onClick={() => navigate(-1)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 500, padding: '8px 4px' }}>
                            <ArrowLeft size={20} />
                            Back
                        </button>
                    ) : (
                        <div style={{ width: 60 }} />
                    )}
                    <img src="/mezgebpos-logo.png" alt="Mezgeb POS" style={{ height: 30, objectFit: 'contain' }} />
                    <div style={{ width: 60 }} />
                </div>

                {/* More menu overlay */}
                {showMore && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 45, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-end' }}
                        onClick={() => setShowMore(false)}>
                        <div style={{ background: sidebarBg, width: '100%', borderRadius: '16px 16px 0 0', padding: '16px 0 80px 0' }}
                            onClick={e => e.stopPropagation()}>
                            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, padding: '0 20px 12px' }}>More</p>
                            {MORE_NAV.map(({ path, label, icon: Icon }) => (
                                <Link key={path} to={path} onClick={() => setShowMore(false)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', color: location.pathname === path ? '#06b6d4' : '#94a3b8', textDecoration: 'none', fontSize: 15, fontWeight: 500 }}>
                                    <Icon size={20} />
                                    {label}
                                </Link>
                            ))}
                            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '8px 20px' }} />
                            <button onClick={() => { setShowMore(false); logout(); }}
                                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', color: '#f87171', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 500, width: '100%', textAlign: 'left' }}>
                                <LogOut size={20} />
                                Log Out
                            </button>
                        </div>
                    </div>
                )}

                {/* Main content */}
                <div style={{ paddingTop: 52, paddingBottom: 64 }}>
                    <Header title={title} />
                    <div style={{ padding: 16 }}>
                        {children}
                    </div>
                </div>

                {/* Bottom navigation */}
                <nav style={{
                    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
                    display: 'flex', borderTop: '1px solid rgba(255,255,255,0.06)',
                    background: sidebarBg, height: 60
                }}>
                    {MOBILE_NAV.map(({ path, label, icon: Icon }) => {
                        const isActive = location.pathname === path;
                        return (
                            <Link key={path} to={path}
                                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, textDecoration: 'none', color: isActive ? '#06b6d4' : '#64748b', fontSize: 10, fontWeight: 500 }}>
                                <Icon size={20} />
                                <span>{label}</span>
                            </Link>
                        );
                    })}
                    <button onClick={() => setShowMore(!showMore)}
                        style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: 'none', border: 'none', cursor: 'pointer', color: showMore ? '#06b6d4' : '#64748b', fontSize: 10, fontWeight: 500 }}>
                        <MoreHorizontal size={20} />
                        <span>More</span>
                    </button>
                </nav>
            </div>
        );
    }

    // Desktop layout
    return (
        <div className="min-h-screen bg-background">
            {isImpersonating && (
                <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between text-sm font-semibold">
                    <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Support Mode — viewing as store owner</span>
                    <Button size="sm" variant="outline" className="border-amber-700 text-amber-950 hover:bg-amber-600 h-7 text-xs" onClick={handleExitSupport}>Exit Support Mode</Button>
                </div>
            )}
            <div className={cn('fixed inset-y-0 left-0 z-40', collapsed ? 'w-16' : 'w-64')}>
                <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
            </div>
            <main className={cn('transition-all duration-300 min-h-screen', collapsed ? 'ml-16' : 'ml-64', isImpersonating && 'pt-10')}>
                <Header title={title} />
                <div className="p-6 max-w-screen-2xl">{children}</div>
            </main>
        </div>
    );
}
